import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { PROTECTED_DRAWER_CLASSNAME, useOnKeyDown } from '../../hooks/useOnKeyPress';
import { usePortal } from '../../hooks/usePortal';
import { useAppwideDrawerContext } from '../../providers/AppwideDrawerProvider';
import { Stack } from '../Core';
import { Drawer, FlexDrawerHeader, TabbedDrawerHeader } from '../Drawer';
import { Text } from '../Text';

export const AppwideDrawer = () => {
  const { drawer, contentSpec } = useAppwideDrawerContext();
  const handleClose = contentSpec?.onClose ?? drawer.close;
  useOnKeyDown({ key: 'Escape' }, drawer.isOpen ? handleClose : undefined, drawer.drawerRef);

  // Whenever the user changes the spec, we guarantee a remount of the drawer content.
  // React / JSX is tricky when you are doing stuff like passing in a function to render a component across react life cycles.
  // Doing this just makes things simpler. There may very well be much better ways of handling this whole work flow.
  const contentSpecHash = useMemo(() => (contentSpec ? uuid() : undefined), [contentSpec]);

  const { setPortalRef } = usePortal('drawer-portal');

  const renderHeader = () => {
    if (contentSpec?.drawerHeaderDesign === 'flex') {
      return (
        <FlexDrawerHeader
          title={
            contentSpec.flexHeaderTitle ? (
              contentSpec.flexHeaderTitle()
            ) : (
              <Text fontWeight="500">{contentSpec.title}</Text>
            )
          }
          actions={contentSpec.renderHeaderContent?.()}
          onClose={handleClose}
          data-testid="app-wide-drawer-title"
          closeButtonTestId="app-wide-drawer-close-button"
        />
      );
    }

    return (
      <TabbedDrawerHeader
        title={contentSpec?.title}
        actions={contentSpec?.renderHeaderContent?.()}
        onClose={handleClose}
        titleTestId="app-wide-drawer-title"
        closeButtonTestId="app-wide-drawer-close-button"
      />
    );
  };

  const drawerClassName = drawer.className
    ? `${drawer.className} ${PROTECTED_DRAWER_CLASSNAME}`
    : PROTECTED_DRAWER_CLASSNAME;
  return (
    <Drawer
      {...drawer}
      drawerHeaderDesign={contentSpec?.drawerHeaderDesign}
      adjustForGapMargin={0}
      className={drawerClassName}
      data-testid="app-wide-drawer"
    >
      {!contentSpec?.hideHeader && renderHeader()}

      <Stack ref={setPortalRef} flex="1" key={contentSpecHash} style={{ overflowY: 'auto' }}>
        {contentSpec?.renderContent?.()}
      </Stack>
    </Drawer>
  );
};
